import { useHydrating } from "eddev/routing"
import { animate, motion, stagger, usePresence } from "motion/react"
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

type Props = ComponentPropsWithoutRef<"div">

export function transitionProps() {
  return {
    "data-transition": "fade",
  }
}

export function Transition(props: Props) {
  return <motion.div {...(props as any)} data-transition="fade" />
}

export function StaggerTransitionRoot(props: Props & { duration?: number; onEnd?: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const ssr = useHydrating()
  const duration = props.duration ?? 0.1
  const [isPresent, safeToRemove] = usePresence()

  if (env.client) {
    useLayoutEffect(() => {
      if (!isPresent) {
        transitionOut(ref.current!).then(() => {
          safeToRemove()
          props.onEnd?.()
        })
      } else {
        // if (!ssr) {
        transitionIn(ref.current!)
        // }
      }
    }, [isPresent])
  }

  return (
    <motion.div
      ref={ref}
      variants={{ hide: {}, show: {} }}
      initial="hide"
      exit="hide"
      animate="show"
      {...(props as any)}
      duration={undefined}
    />
  )
}

export function ScrollReveal(props: PropsWithChildren<ComponentProps<typeof motion.div>>) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(env.admin ? true : false)

  useEffect(() => {
    if (env.admin) return
    let timer: NodeJS.Timeout = 0 as any
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisible(true)
            }, 100)
            // io.disconnect()
          }
        })
      },
      { threshold: 0.1 },
    )

    io.observe(ref.current!)

    return () => {
      io.disconnect()
      clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    if (visible && ref.current) {
      transitionIn(ref.current!, 2)
    }
  }, [visible, ref.current])

  return (
    <motion.div
      ref={ref}
      {...props}
      style={{ "--factor": env.admin ? 1 : 0 } as any}
      data-transition-blocked={visible ? undefined : ""}
    ></motion.div>
  )
}

function getTransitionElements(el: Element) {
  const wh = window.innerHeight
  const ww = window.innerWidth
  const els: { el: HTMLElement; rect: DOMRect; mode: string }[] = []
  const addElement = (el: HTMLElement, mode: string, force = false) => {
    const rect = el.getBoundingClientRect()
    const onScreen =
      rect.top < wh && rect.bottom > 0 && rect.left < ww && rect.right > 0 && rect.height > 0 && rect.width > 0
    if (onScreen || force) {
      els.push({ el: el as HTMLElement, rect, mode })
    }
  }
  el.querySelectorAll("[data-transition]:not([data-transition-blocked] [data-transition])").forEach((el) => {
    let mode = el.getAttribute("data-transition") ?? "fade"
    if (mode.startsWith("children/")) {
      mode = mode.replace("children/", "")
      el.childNodes.forEach((el) => {
        if (el instanceof HTMLElement) {
          addElement(el, mode, true)
        }
      })
    } else {
      addElement(el as HTMLElement, mode)
    }
  })
  els.sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left)
  return els
}

const ELEMENT_FADE_IN_DURATION = 0.3
const ELEMENT_FADE_OUT_DURATION = 0.2
const FADE_IN_DURATION = 0.7
const FADE_OUT_DURATION = 0.3

function transitionIn(el: HTMLElement, durationScale = 1) {
  const els = getTransitionElements(el)
  el.style.setProperty("--factor", "1")
  if (env.admin) return Promise.resolve()
  if (els.length === 0) return Promise.resolve()
  els.forEach((el, i) => {
    el.el.style.setProperty("--factor", "0")
  })
  el.style.setProperty("--factor", "1")
  return animate(
    els.map((el, i) => el.el),
    { "--factor": 1 },
    {
      ease: "easeOut",
      duration: ELEMENT_FADE_IN_DURATION * durationScale,
      delay: stagger(((FADE_IN_DURATION - ELEMENT_FADE_IN_DURATION) * durationScale) / els.length),
    },
  )
}

function transitionOut(el: HTMLElement, durationScale = 1) {
  const els = getTransitionElements(el)
  if (els.length === 0) return Promise.resolve()
  els.forEach((el, i) => {
    el.el.style.setProperty("--factor", el.el.style.getPropertyValue("--factor") || "1")
  })
  el.style.setProperty("--factor", "0")
  animate(
    els.map((el, i) => el.el),
    { "--factor": 0 },
    {
      ease: "easeIn",
      duration: ELEMENT_FADE_OUT_DURATION,
      delay: stagger(((FADE_OUT_DURATION - ELEMENT_FADE_OUT_DURATION) * durationScale) / els.length),
    },
  )
  // resolve promise after total duration
  return new Promise((resolve) => {
    const totalDuration =
      ELEMENT_FADE_OUT_DURATION +
      (((FADE_OUT_DURATION - ELEMENT_FADE_OUT_DURATION) * durationScale) / els.length) * els.length
    setTimeout(() => {
      resolve(null)
    }, totalDuration * 1000)
  })
}
